import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { UrlConstants } from "../../utils/CommonUtils/constants";
import { getCreateAssessmentRequestBody } from "../../utils/helper";
import * as Sentry from "@sentry/browser";
import createAssessmentApi from "../../fetchApi/createAssessmentApi";
import useTranslationSetup from "../../utils/useTranslationSetup";
import useParamValues from "../../utils/useParamValues";
import girlReading from "../../assets/girl-reading.svg";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CustomErrorDialogBox from "../../components/CustomErrorDialogBox/CustomErrorDialogBox";
import useWebViewBridge from "../../utils/useWebViewBridge";
import "./AssessmentLoadingScreen.css";

function AssessmentLoadingScreen({
  setCreateAssessmentResponse,
  latitude,
  longitude,
}) {
  const { t } = useTranslationSetup();
  const urlParams = useParamValues();
  const { sendMessageToReactNative } = useWebViewBridge();
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [reload, setReload] = useState(true);

  const handleRetryClick = () => {
    setErrorDialogOpen(false);
    setCreateAssessmentResponse(null);
    setReload(true);
  };

  useEffect(() => {
    if (reload) {
      setTimeout(() => {
        setReload(false);
        (async () => {
          try {
            const response = await createAssessmentApi(
              `${UrlConstants.BASE_URL}${UrlConstants.BASE_PATH_URL}${UrlConstants.ASSESSMENT}`,
              getCreateAssessmentRequestBody(urlParams, latitude, longitude),
              urlParams?.token
            );

            if (response.ok || response.status === "CREATED") {
              setCreateAssessmentResponse(response);
            } else {
              setErrorDialogOpen(true);
              Sentry.captureException(
                new Error("API error", {
                  cause: JSON.stringify(response),
                }),
                {
                  level: "error",
                  extra: {
                    responseBody: JSON.stringify(response),
                  },
                }
              );
            }
          } catch (error) {
            setErrorDialogOpen(true);
            Sentry.captureException(error);
          }
        })();
      }, 3000);
    }
  }, [reload]);

  return (
    <>
      <div className="assessment-loading-screen-container">
        <div className="assessment-loading-screen-heading">
          {t("assessment_loading_screen")}
        </div>
        <div className="assessment-loading-image">
          <img src={girlReading} alt="girl-reading" />
        </div>
        <div className="assessment-loading-info">
          <div className="assessment-loading-student-name">
            {urlParams?.studentName}
          </div>
          <div className="assessment-loading-student-grade">
            {t("grade_text", { grade: urlParams?.classNum })}
          </div>
        </div>
        <div className="assessment-loading-indicator">
          {t("loading_text")}
          <CircularProgress size={15} />
        </div>
      </div>
      <CustomErrorDialogBox
        header={t("something_went_wrong")}
        icon={
          <ErrorOutlineOutlinedIcon
            style={{
              color: "#D95959",
              borderRadius: "10px",
              marginTop: "16px",
              width: "80px",
              height: "80px",
            }}
          />
        }
        primaryButtonText={t("retake")}
        secondaryButtonText={t("new_student")}
        isDialogOpen={errorDialogOpen}
        showPrimaryButton
        showSecondaryButton
        onClickPrimaryButton={handleRetryClick}
        onClickSecondaryButton={() => sendMessageToReactNative("status=FAILED")}
      />
    </>
  );
}

export default AssessmentLoadingScreen;
